import moment from 'moment';
import omit from 'lodash/omit';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  showNotification,
  NOTIFICATION_TYPE,
  Button,
  ButtonsWrapper,
  Form,
  Separator,
} from '@thinknet-gmbh/platform-common-ui';

import { CreateProjectInfo } from 'src/pages/projects/components/create-project-info';
import { CreateTask } from 'src/pages/projects/components/create-task';
import { ITask } from 'src/pages/projects/projects.interfaces';
import { useUpsertProject } from 'src/hooks/projects/upsert-project.hook';

import { IProjectDetailsEditProps } from './project-details-edit.interfaces';

const ProjectDetailsEdit = ({ project, taskTemplatesIds, onCancel }: IProjectDetailsEditProps) => {
  const { t } = useTranslation(['common', 'projects']);
  const [form] = Form.useForm();
  const [selectedGantts, setSelectedGantts] = useState<string[]>(project.ganttIds || []);

  const { upsertProject, loading } = useUpsertProject({
    onSuccess: () => {
      onCancel();
      showNotification({
        type: NOTIFICATION_TYPE.success,
        message: t('projects:notifications.projectWasUpdated.title'),
      });
    },
  });

  const onFinish = (values: any) => {
    const projectNumberErrors = form.getFieldError('projectNumber');

    if (!projectNumberErrors.length) {
      upsertProject({
        variables: {
          payload: {
            ...omit(values, ['projectPlannedHours']),
            _id: project._id,
            companyId: project.companyId,
            tasks:
              values.tasks?.map((t: any) => ({
                ...omit(t, ['plannedHours']),
                files: t.files?.fileList?.map((f: any) => f.originFileObj).filter(Boolean) || [],
              })) || [],
          },
        },
      });
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      initialValues={{
        ...project,
        startDate: project.startDate ? moment(project.startDate) : null,
        tasks: project.tasks!.map((task: ITask) => ({
          ...task,
          files:
            task?.files?.map(file => ({
              uid: file._id,
              url: file.location,
              name: file.filename || file.key,
              fileName: file.filename,
            })) || [],
          timeStripes: task?.timeStripes.map(({ gantt, ...stripe }) => ({
            ...stripe,
            startDate: moment(stripe.startDate),
          })),
        })),
      }}
    >
      <CreateProjectInfo
        deputyMemberId={project.deputyMemberId}
        projectNumber={project.projectNumber}
        update
        form={form}
        onSelectGantts={(values: string[]) => setSelectedGantts(values)}
      />
      <Form.List name="tasks">
        {(fields, { remove, add }) => (
          <CreateTask
            project={project}
            taskTemplatesIds={taskTemplatesIds}
            fields={fields}
            removeTask={remove}
            addTask={add}
            form={form}
            selectedGantts={selectedGantts}
          />
        )}
      </Form.List>
      <Separator />
      <ButtonsWrapper marginTop={24}>
        <Button htmlType="submit" type="primary" loading={loading}>
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default ProjectDetailsEdit;
